import { Users, TrendingUp, DollarSign } from "lucide-react";
import { formatNumber, formatCurrency } from "@/lib/constants";
import { useTheme } from "@/contexts/ThemeContext";

export default function TotalsBar({ totalPatients, totalWrvus, hourlyPay = 0, retentionRate = 0.76, wrvuRate = 25 }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const wrvuPay = totalWrvus * wrvuRate;
  const preTax = wrvuPay + hourlyPay;
  const postTax = preTax * retentionRate;

  return (
    <div
      data-testid="totals-bar"
      style={{ 
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.98)',
        borderTopColor: isDark ? 'rgba(51, 65, 85, 0.5)' : 'rgba(209, 213, 219, 1)'
      }}
      className={`totals-bar border-t px-5 py-5 backdrop-blur-lg ${isDark ? '' : 'shadow-lg'}`}
    >
      <div className="max-w-5xl mx-auto flex items-center justify-start">
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${
              isDark ? 'bg-blue-500/15' : 'bg-blue-100'
            }`}>
              <Users size={24} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
            </div>
            <div>
              <p className={`text-xs uppercase tracking-widest font-semibold ${
                isDark ? 'text-slate-500' : 'text-gray-500'
              }`}>Patients</p>
              <p data-testid="total-patients" className={`text-2xl font-mono font-bold ${
                isDark ? 'text-slate-100' : 'text-gray-900'
              }`}>{totalPatients.toLocaleString()}</p>
            </div>
          </div>
          <div className={`w-px h-12 ${isDark ? 'bg-slate-700/50' : 'bg-gray-300'}`} />
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${
              isDark ? 'bg-emerald-500/15' : 'bg-emerald-100'
            }`}>
              <TrendingUp size={24} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />
            </div>
            <div>
              <p className={`text-xs uppercase tracking-widest font-semibold ${
                isDark ? 'text-slate-500' : 'text-gray-500'
              }`}>wRVUs</p>
              <p data-testid="total-wrvus" className={`text-2xl font-mono font-bold ${
                isDark ? 'text-emerald-400' : 'text-emerald-600'
              }`}>{formatNumber(totalWrvus)}</p>
            </div>
          </div>
          <div className={`w-px h-12 ${isDark ? 'bg-slate-700/50' : 'bg-gray-300'}`} />
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${
              isDark ? 'bg-amber-500/15' : 'bg-amber-100'
            }`}>
              <DollarSign size={24} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
            </div>
            <div>
              <p className={`text-xs uppercase tracking-widest font-semibold ${
                isDark ? 'text-slate-500' : 'text-gray-500'
              }`}>Pre-Tax $</p>
              <p data-testid="total-pretax" className={`text-2xl font-mono font-bold ${
                isDark ? 'text-amber-400' : 'text-amber-600'
              }`}>{formatCurrency(preTax)}</p>
            </div>
          </div>
          <div className={`w-px h-12 ${isDark ? 'bg-slate-700/50' : 'bg-gray-300'}`} />
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${
              isDark ? 'bg-sky-500/15' : 'bg-sky-100'
            }`}>
              <DollarSign size={24} className={isDark ? 'text-sky-400' : 'text-sky-600'} />
            </div>
            <div>
              <p className={`text-xs uppercase tracking-widest font-semibold ${
                isDark ? 'text-slate-500' : 'text-gray-500'
              }`}>Post-Tax $</p>
              <p data-testid="total-posttax" className={`text-2xl font-mono font-bold ${
                isDark ? 'text-sky-400' : 'text-sky-600'
              }`}>{formatCurrency(postTax)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
